import type { JSX } from "preact";
import { Editor, Element, Transforms } from "slate";
import { ReactEditor, useSlate } from "slate-react";
import type { Icon } from "../components/icons";
import { ButtonIcon } from "./ButtonIcon";
import type { BlockButtonType, ParagraphElement } from "./types";
import { isAlignType, isListType } from "./types";

interface Props {
    type: BlockButtonType;
    icon: Icon;
}

export function BlockButton({ type, icon }: Props): JSX.Element {
    const editor = useSlate();
    return (
        <ButtonIcon
            active={isBlockActive(editor, type)}
            icon={icon}
            onClick={(event) => {
                event.preventDefault();
                toggleBlock(editor, type);
            }}
        />
    );
}

function isBlockActive(editor: Editor, type: BlockButtonType): boolean {
    if (editor.selection == null) {
        return false;
    }
    const matches = Array.from(
        Editor.nodes(editor, {
            at: Editor.unhangRange(editor, editor.selection),
            match: (n) =>
                !Editor.isEditor(n) && Element.isElement(n) && isType(n, type),
        }),
    );
    return matches.length > 0;
}

function isType(element: Element, type: BlockButtonType): boolean {
    if (isAlignType(type)) {
        return isParagraphElement(element) && element.align === type;
    }
    return element.type === type;
}

function toggleBlock(editor: Editor, type: BlockButtonType) {
    const isActive = isBlockActive(editor, type);
    const isList = isListType(type);

    Transforms.unwrapNodes(editor, {
        match: (n) =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            isListType(n.type) &&
            !isAlignType(type),
        split: true,
    });

    let newProperties: Partial<Element>;
    if (isAlignType(type)) {
        newProperties = {
            align: isActive ? undefined : type,
        };
    } else if (isActive) {
        newProperties = {
            type: "paragraph",
        };
    } else {
        newProperties = {
            type: isList ? "list-item" : type,
        };
    }

    Transforms.setNodes<Element>(editor, newProperties);

    if (!isActive && isList) {
        const block: ParagraphElement = {
            type: getListType(type),
            children: [],
        };
        Transforms.wrapNodes(editor, block);
    }

    // Return focus to editor
    ReactEditor.focus(editor);
}

function isParagraphElement(element: Element): element is ParagraphElement {
    return "type" in element;
}

function getListType(type: BlockButtonType): "numbered-list" | "bulleted-list" {
    if (type === "numbered-list" || type === "bulleted-list") {
        return type;
    }
    throw new Error(`Expected list type, got ${type}`);
}
